/**
 * Money Cycle Data Selectors
 * 
 * Fetches data exclusively from My Numbers module.
 * This is the single source of truth for the Money Cycle visualization.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Get auth headers for API calls
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const useMoneyCycleData = () => {
  const [data, setData] = useState({
    currency: "USD",
    income: 0,
    expenses: 0,
    savingsMonthly: 0,
    passiveIncomeMonthly: 0,
    emergencyFundBalance: 0,
    totalDebtPaymentMonthly: 0,
    totalInvestments: 0,
    totalDebtBalance: 0,
    unproductiveAssets: 0,
    hasData: false,
    dataSource: 'none',
  });
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen for My Numbers updates to refresh data
  useEffect(() => {
    const handleMyNumbersUpdate = () => {
      console.log('My Numbers updated, refreshing Money Cycle data...');
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    return () => {
      window.removeEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchMoneyCycleData = async () => {
      try {
        setLoading(true);

        // Fetch My Numbers data - the ONLY source for Money Cycle
        const myNumbersResponse = await axios.get(`${API}/api/my-numbers`, {
          headers: getAuthHeaders()
        });
        
        if (myNumbersResponse.data && myNumbersResponse.data.monthly_income !== undefined) {
          const myNumbers = myNumbersResponse.data;
          
          // Get income and expenses
          let income = myNumbers.monthly_income || 0;
          let expenses = myNumbers.monthly_expenses || 0;
          
          // If using ranges, convert to approximate values
          if (myNumbers.input_mode === 'range') {
            if (myNumbers.monthly_income_range && !myNumbers.monthly_income) {
              income = convertRangeToValue(myNumbers.monthly_income_range);
            }
            if (myNumbers.monthly_expenses_range && !myNumbers.monthly_expenses) {
              expenses = convertRangeToValue(myNumbers.monthly_expenses_range);
            }
          }
          
          // Calculate investments total
          const totalInvestments = (myNumbers.assets?.investments || 0) + 
                                  (myNumbers.assets?.retirement_accounts || 0);
          
          // Passive income
          const passiveIncomeMonthly = myNumbers.monthly_investment_income || 0;
          
          // Debt
          const totalDebtBalance = myNumbers.total_debt || 0;
          const totalDebtPaymentMonthly = myNumbers.total_monthly_debt_payments || 0;
          
          // Emergency fund - use dedicated field if available, otherwise fall back to savings + checking
          const emergencyFundBalance = (myNumbers.assets?.emergency_fund || 0) || 
                                      ((myNumbers.assets?.savings || 0) + 
                                      (myNumbers.assets?.checking || 0));
          
          // Unproductive assets (real estate, vehicles that don't generate income)
          const unproductiveAssets = (myNumbers.assets?.real_estate || 0) + 
                                    (myNumbers.assets?.vehicles || 0);
          
          // Calculate monthly savings
          const surplus = income - expenses;
          const savingsMonthly = Math.max(0, surplus);

          setData({
            currency: myNumbers.currency || 'USD',
            income,
            expenses,
            savingsMonthly,
            passiveIncomeMonthly,
            investmentIncome: passiveIncomeMonthly,
            emergencyFundBalance,
            emergencyFund: emergencyFundBalance,
            totalDebtPaymentMonthly,
            debtPayments: totalDebtPaymentMonthly,
            totalInvestments,
            totalDebtBalance,
            totalDebt: totalDebtBalance,
            unproductiveAssets,
            hasData: true,
            dataSource: 'my-numbers',
          });
        } else {
          // No My Numbers data available
          setData({
            currency: "USD",
            income: 0,
            expenses: 0,
            savingsMonthly: 0,
            passiveIncomeMonthly: 0,
            emergencyFundBalance: 0,
            totalDebtPaymentMonthly: 0,
            totalInvestments: 0,
            totalDebtBalance: 0,
            unproductiveAssets: 0,
            hasData: false,
            dataSource: 'none',
          });
        }
      } catch (error) {
        console.error('Error fetching My Numbers data for Money Cycle:', error);
        
        // No data available
        setData({
          currency: "USD",
          income: 0,
          expenses: 0,
          savingsMonthly: 0,
          passiveIncomeMonthly: 0,
          emergencyFundBalance: 0,
          totalDebtPaymentMonthly: 0,
          totalInvestments: 0,
          totalDebtBalance: 0,
          unproductiveAssets: 0,
          hasData: false,
          dataSource: 'none',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMoneyCycleData();
  }, [refreshTrigger]);

  return { data, loading };
};

// Helper function to convert range strings to approximate values
const convertRangeToValue = (rangeString) => {
  if (!rangeString) return 0;
  
  const rangeMap = {
    // Income ranges
    '0-1000': 500,
    '1000-2500': 1750,
    '2500-5000': 3750,
    '5000-7500': 6250,
    '7500-10000': 8750,
    '10000-15000': 12500,
    '15000-25000': 20000,
    '25000-50000': 37500,
    '50000+': 75000,
    // Expense ranges
    '1000-2000': 1500,
    '2000-3500': 2750,
    '3500-5000': 4250,
    '5000-7500': 6250,
    '7500-10000': 8750,
    '10000-15000': 12500,
    '15000+': 20000,
    // Asset ranges
    '1000-5000': 3000,
    '5000-10000': 7500,
    '10000-25000': 17500,
    '25000-50000': 37500,
    '50000-100000': 75000,
    '100000-250000': 175000,
    '250000-500000': 375000,
    '500000-1000000': 750000,
    '1000000+': 1500000,
    // Debt ranges
    '0': 0,
    '1-5000': 2500,
    '5000-10000': 7500,
    '10000-25000': 17500,
    '25000-50000': 37500,
    '50000-100000': 75000,
    '100000-250000': 175000,
    '250000-500000': 375000,
    '500000+': 750000,
    // Payment ranges
    '1-250': 125,
    '250-500': 375,
    '500-1000': 750,
    '1000-2000': 1500,
    '2000-3500': 2750,
    '3500-5000': 4250,
    '5000-7500': 6250,
    '7500+': 10000,
    // Investment income ranges
    '1-100': 50,
    '100-500': 300,
    '500-1000': 750,
    '1000-2500': 1750,
    '2500-5000': 3750,
    '5000-10000': 7500,
    '10000+': 15000,
  };
  
  return rangeMap[rangeString] || 0;
};

export default useMoneyCycleData;
